/**
 * AdminFicStats — Statistiche Fatture in Cloud.
 * Mostra l'ultimo snapshot sincronizzato (con data sincronizzazione).
 * "Aggiorna" risincronizza da FiC e mostra il delta crediti rispetto
 * alla sincronizzazione precedente.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Receipt, RefreshCw, Loader2, AlertCircle, AlertTriangle, TrendingUp, TrendingDown, Minus, Clock } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api, formatApiErrorDetail } from "../lib/api";

const eur = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function DeltaBadge({ delta, previousSyncedAt }) {
  if (delta === null || delta === undefined) return null;
  const abs = Math.abs(delta);
  if (abs < 0.005) {
    return (
      <div className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm bg-slate-100 text-slate-600" data-testid="fic-delta-badge">
        <Minus size={12} /> Invariato rispetto alla sincronizzazione del {fmtDateTime(previousSyncedAt)}
      </div>
    );
  }
  const up = delta > 0;
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-sm ${up ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}
      data-testid="fic-delta-badge"
    >
      {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
      <span className="font-semibold">{up ? "+" : "−"}{eur.format(abs)}</span>
      <span>crediti {up ? "aumentati" : "diminuiti"} rispetto al {fmtDateTime(previousSyncedAt)}</span>
    </div>
  );
}

function TopList({ title, subtitle, rows, testid }) {
  const max = rows.length ? rows[0].pct : 0;
  return (
    <div className="bg-white border border-ink/10 rounded-sm" data-testid={testid}>
      <div className="p-4 border-b border-ink/10">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">{title}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-500">Nessun dato disponibile</div>
      ) : (
        <div className="p-3 space-y-1">
          {rows.map((r, i) => (
            <div key={r.name} className="px-2 py-1.5 rounded-sm hover:bg-cream2/60" data-testid={`${testid}-row-${i}`}>
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] text-slate-400 font-mono w-5 flex-shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-medium text-ink truncate" title={r.name}>{r.name}</span>
                </div>
                <div className="flex items-baseline gap-2 flex-shrink-0">
                  <span className="text-slate-700 whitespace-nowrap">{eur.format(r.amount)}</span>
                  <span className="text-[11px] font-semibold text-gold whitespace-nowrap w-12 text-right">{r.pct}%</span>
                </div>
              </div>
              <div className="ml-7 mt-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-ink/70 rounded-full" style={{ width: `${max ? Math.max(2, (r.pct / max) * 100) : 0}%` }} />
              </div>
              {r.by_year && Object.keys(r.by_year).length > 0 && (
                <div className="ml-7 mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500" data-testid={`${testid}-row-${i}-by-year`}>
                  {Object.entries(r.by_year).map(([y, v]) => (
                    <span key={y} className="whitespace-nowrap">
                      <span className="font-semibold text-slate-600">{y}</span> {eur.format(v)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const INK = "#0F172A";
const GOLD = "#B45309";

function VatPie({ label, data, highlight }) {
  const pieData = [
    { name: "Con IVA · Italia", value: data.con_iva, pct: data.con_iva_pct },
    { name: "IVA zero · Export", value: data.iva_zero, pct: data.iva_zero_pct },
  ];
  return (
    <div
      className={`rounded-sm p-4 flex flex-col items-center ${highlight ? "bg-ink text-cream" : "bg-cream2/60"}`}
      data-testid={`fic-vat-pie-${label}`}
    >
      <div className={`text-[11px] tracking-[0.18em] uppercase font-semibold ${highlight ? "text-cream" : "text-ink"}`}>
        {highlight ? "Totale · tutti gli anni" : label}
      </div>
      <div className={`text-[11px] mt-0.5 ${highlight ? "text-cream/70" : "text-slate-500"}`}>{eur.format(data.total)}</div>
      <div className="w-full h-[150px] mt-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              innerRadius={38}
              outerRadius={62}
              paddingAngle={2}
              stroke="none"
            >
              <Cell fill={highlight ? "#F9F8F6" : INK} />
              <Cell fill={GOLD} />
            </Pie>
            <Tooltip
              formatter={(value, name, item) => [`${eur.format(value)} (${item?.payload?.pct}%)`, name]}
              contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid rgba(15,23,42,0.1)" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-0.5 text-[11px] mt-1 w-full">
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 ${highlight ? "text-cream/85" : "text-slate-600"}`}>
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: highlight ? "#F9F8F6" : INK }} /> Italia
          </span>
          <span className={`font-semibold ${highlight ? "text-cream" : "text-ink"}`}>{data.con_iva_pct}%</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-1.5 ${highlight ? "text-cream/85" : "text-slate-600"}`}>
            <span className="w-2 h-2 rounded-sm inline-block" style={{ background: GOLD }} /> Export
          </span>
          <span className="font-semibold" style={{ color: GOLD }}>{data.iva_zero_pct}%</span>
        </div>
      </div>
    </div>
  );
}

function VatSplit({ years, totalSplit }) {
  if (!years?.length) return null;
  return (
    <div className="bg-white border border-ink/10 rounded-sm" data-testid="fic-vat-split">
      <div className="p-4 border-b border-ink/10">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Vendite Italia vs Export</div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Fatturato netto: con IVA (Italia) vs IVA zero (Export) · per anno e distribuzione totale
        </div>
      </div>
      <div className="p-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {years.map((y) => (
          <VatPie key={y.year} label={y.year} data={y} />
        ))}
        {totalSplit && <VatPie label="totale" data={totalSplit} highlight />}
      </div>
    </div>
  );
}

export default function AdminFicStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/fatture-cloud/stats/credits${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Errore caricamento statistiche");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center gap-3 text-slate1" data-testid="fic-stats-loading">
        <Loader2 size={26} className="animate-spin" />
        <div className="text-[11px] tracking-[0.2em] uppercase">Carico gli ultimi dati sincronizzati…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-ink/10 rounded-sm p-8 flex items-start gap-3" data-testid="fic-stats-error">
        <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold text-ink">Impossibile caricare i dati</div>
          <div className="text-xs text-slate-600 mt-1">{error}</div>
          <button
            type="button"
            onClick={() => load()}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm"
            data-testid="fic-stats-retry"
          >
            <RefreshCw size={12} /> Riprova
          </button>
        </div>
      </div>
    );
  }

  const invoices = data?.invoices || [];

  // Raggruppa le fatture per cliente: gruppi ordinati per credito totale
  // discendente, fatture interne ordinate per scadenza crescente.
  const clientGroups = (() => {
    const map = new Map();
    for (const inv of invoices) {
      const key = inv.client_name || "—";
      if (!map.has(key)) map.set(key, { client: key, total: 0, overdueTotal: 0, overdueCount: 0, invoices: [] });
      const g = map.get(key);
      g.total += inv.amount_outstanding || 0;
      if (inv.overdue) {
        g.overdueTotal += inv.amount_outstanding || 0;
        g.overdueCount += 1;
      }
      g.invoices.push(inv);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => b.total - a.total);
    arr.forEach((g) => g.invoices.sort((a, b) => (a.next_due_date || "9999").localeCompare(b.next_due_date || "9999")));
    return arr;
  })();

  return (
    <div className="space-y-6" data-testid="fic-stats-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 flex items-center gap-1.5">
            <Receipt size={12} /> Statistiche Fatture in Cloud
            {data?.company_name && <span className="text-slate-400 normal-case tracking-normal">· {data.company_name}</span>}
          </div>
          <h2 className="font-display text-2xl text-ink mt-1">Crediti da incassare</h2>
          <div className="text-xs text-slate-500 mt-0.5">Fatture di vendita non saldate o saldate parzialmente · dal {fmtDate(data?.from_date)}</div>
          <div className="text-xs text-slate-600 mt-1.5 flex items-center gap-1.5" data-testid="fic-synced-at">
            <Clock size={11} className="text-slate-400" />
            Ultima sincronizzazione: <span className="font-semibold">{fmtDateTime(data?.synced_at)}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors disabled:opacity-60"
          data-testid="fic-stats-refresh"
        >
          {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          {refreshing ? "Sincronizzo…" : "Aggiorna"}
        </button>
      </div>

      <DeltaBadge delta={data?.delta} previousSyncedAt={data?.previous_synced_at} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-ink text-cream rounded-sm p-5" data-testid="fic-total-outstanding">
          <div className="text-[10px] tracking-[0.2em] uppercase text-cream/60">Totale crediti</div>
          <div className="font-display text-3xl mt-2">{eur.format(data?.total_outstanding || 0)}</div>
          <div className="text-[11px] text-cream/60 mt-1">{data?.count || 0} fatture aperte</div>
        </div>
        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="fic-overdue-total">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-rose-600" /> Scaduto
          </div>
          <div className="font-display text-3xl mt-2 text-rose-700">{eur.format(data?.overdue_total || 0)}</div>
          <div className="text-[11px] text-slate-500 mt-1">{data?.overdue_count || 0} fatture oltre scadenza</div>
        </div>
        <div className="bg-white border border-ink/10 rounded-sm p-5" data-testid="fic-not-due-total">
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">A scadere</div>
          <div className="font-display text-3xl mt-2 text-ink">
            {eur.format(Math.max(0, (data?.total_outstanding || 0) - (data?.overdue_total || 0)))}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">{Math.max(0, (data?.count || 0) - (data?.overdue_count || 0))} fatture non ancora scadute</div>
        </div>
      </div>

      {(data?.top_clients?.length > 0 || data?.top_suppliers?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopList
            title="Top Clienti · Fatturato"
            subtitle={`% sul totale vendite dal ${(data?.tops_from_date || "2023-01-01").slice(0, 4)}: ${eur.format(data?.sales_total || 0)}`}
            rows={data?.top_clients || []}
            testid="fic-top-clients"
          />
          <TopList
            title="Top Fornitori · Acquisti"
            subtitle={`% sul totale acquisti dal ${(data?.tops_from_date || "2023-01-01").slice(0, 4)}: ${eur.format(data?.purchases_total || 0)} · Arrital accorpata in We Do Home`}
            rows={data?.top_suppliers || []}
            testid="fic-top-suppliers"
          />
        </div>
      )}

      <VatSplit years={data?.vat_split_by_year} totalSplit={data?.vat_split_total} />

      <div className="bg-white border border-ink/10 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-ink/10 text-[11px] tracking-[0.2em] uppercase text-slate1">
          Elenco crediti ({invoices.length} fatture · {clientGroups.length} clienti) · clienti dal credito più alto
        </div>
        {invoices.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="fic-no-credits">
            Nessun credito aperto: tutte le fatture dal 2024 risultano saldate. 🎉
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fic-credits-table">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-slate1 border-b border-ink/10">
                  <th className="px-4 py-3">Cliente / Fattura</th>
                  <th className="px-4 py-3">Data</th>
                  <th className="px-4 py-3">Scadenza</th>
                  <th className="px-4 py-3 text-right">Totale</th>
                  <th className="px-4 py-3 text-right">Incassato</th>
                  <th className="px-4 py-3 text-right">Residuo</th>
                </tr>
              </thead>
              <tbody>
                {clientGroups.map((g, gi) => (
                  <React.Fragment key={g.client}>
                    <tr className="bg-cream2/80 border-b border-ink/10" data-testid={`fic-client-group-${gi}`}>
                      <td colSpan={5} className="px-4 py-2.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] text-slate-400 font-mono">{String(gi + 1).padStart(2, "0")}</span>
                          <span className="font-semibold text-ink" title={g.client}>{g.client}</span>
                          <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                            {g.invoices.length} fattur{g.invoices.length === 1 ? "a" : "e"}
                          </span>
                          {g.overdueCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-[9px] tracking-wider uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5 py-0.5" data-testid={`fic-client-group-${gi}-overdue`}>
                              <AlertTriangle size={9} /> {g.overdueCount} scadut{g.overdueCount === 1 ? "a" : "e"} · {eur.format(g.overdueTotal)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right font-display font-semibold text-ink whitespace-nowrap" data-testid={`fic-client-group-${gi}-total`}>
                        {eur.format(g.total)}
                      </td>
                    </tr>
                    {g.invoices.map((inv) => (
                      <tr key={inv.id} className="border-b border-ink/5 hover:bg-cream2/50" data-testid={`fic-credit-row-${inv.id}`}>
                        <td className="px-4 py-2.5 pl-11 text-slate-600 whitespace-nowrap">
                          n. {inv.number}
                          {inv.partially_paid && (
                            <span className="ml-2 inline-block text-[9px] tracking-wider uppercase bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">Parziale</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(inv.date)}</td>
                        <td className={`px-4 py-2.5 whitespace-nowrap ${inv.overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>
                          {fmtDate(inv.next_due_date)}
                          {inv.overdue && <AlertTriangle size={11} className="inline ml-1.5 -mt-0.5 text-rose-600" />}
                        </td>
                        <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{eur.format(inv.amount_gross)}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600 whitespace-nowrap">{eur.format(inv.amount_paid)}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-ink whitespace-nowrap">{eur.format(inv.amount_outstanding)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
